import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { heroSlides } from '@/mocks/anime';

export default function Hero() {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const total = heroSlides.length;

  const goTo = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrent(index);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning]);

  const next = useCallback(() => goTo((current + 1) % total), [current, total, goTo]);
  const prev = useCallback(() => goTo((current - 1 + total) % total), [current, total, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  const slide = heroSlides[current];

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden" aria-label="精选推荐">
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50 z-10"></div>
      <div
        className="absolute inset-0 transition-opacity duration-700"
        style={{
          backgroundImage: `url(${slide.imageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      ></div>

      <div className="relative z-20 w-full h-full flex flex-col items-center justify-center text-center px-4 md:px-6">
        <div className="max-w-4xl w-full">
          <span className="inline-block px-4 py-1 rounded-full bg-accent-500/90 text-background-50 text-xs md:text-sm font-medium mb-4 md:mb-6">
            {slide.category}
          </span>
          <h2 className="font-heading text-2xl md:text-5xl text-background-50 mb-3 md:mb-5 leading-tight">
            {slide.title}
          </h2>
          <p className="text-background-50/80 text-sm md:text-lg mb-6 md:mb-8 max-w-2xl mx-auto">
            {slide.subtitle}
          </p>
          <Link
            to={`/article/${slide.slug}`}
            className="inline-flex items-center gap-2 px-6 md:px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-full text-sm md:text-base transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-play-circle-line text-lg"></i>
            立即观看
          </Link>
        </div>
      </div>

      <button
        onClick={prev}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-30 w-10 h-10 md:w-12 md:h-12 rounded-full bg-background-50/20 hover:bg-background-50/40 backdrop-blur-sm flex items-center justify-center transition-all cursor-pointer"
        aria-label="上一个"
      >
        <i className="ri-arrow-left-s-line text-xl text-background-50"></i>
      </button>
      <button
        onClick={next}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-30 w-10 h-10 md:w-12 md:h-12 rounded-full bg-background-50/20 hover:bg-background-50/40 backdrop-blur-sm flex items-center justify-center transition-all cursor-pointer"
        aria-label="下一个"
      >
        <i className="ri-arrow-right-s-line text-xl text-background-50"></i>
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2">
        {heroSlides.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`transition-all duration-300 rounded-full cursor-pointer ${
              i === current ? 'w-8 h-2.5 bg-primary-500' : 'w-2.5 h-2.5 bg-background-50/40 hover:bg-background-50/60'
            }`}
            aria-label={`切换到第${i + 1}张`}
          ></button>
        ))}
      </div>
    </section>
  );
}